import React, { useState, useEffect } from "react";
import {
    Box, Typography, Button, TextField, Paper, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from "@mui/material";

import { db, storage } from "../firebase";
import { ref, getDownloadURL, uploadBytesResumable } from "firebase/storage";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";

import UploadFileIcon from "@mui/icons-material/UploadFile";

const colors = {
    darkGreen: '#06362D',
    calPolyGreen: '#155332',
    vanilla: '#DEE2B1',
    gold: '#FFD500',
    goldenrod: '#D1A505'
};

function Uploaddocuments() {

    const [userId, setUserId] = useState(null);
    const [propertyId, setPropertyId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);

    const [documentData, setDocumentData] = useState({});
    const [accountDetails, setAccountDetails] = useState({
        acc_no: "", acc_name: "", ifsc_code: "", branch: ""
    });

    const [message, setMessage] = useState("");

    // -------------------------------------------
    // Load logged-in user details
    // -------------------------------------------
    useEffect(() => {
        const auth = getAuth();
        const currentUser = auth.currentUser;

        if (!currentUser) return;

        const uid = currentUser.uid;
        setUserId(uid);

        const loadData = async () => {
            const userRef = doc(db, "authroles", uid);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
                const user = userSnap.data();
                setPropertyId(user.propertyId);

                // Load existing documents (if available)
                const documentRef = doc(db, "documents", `${uid}_${user.propertyId}`);
                const docSnap = await getDoc(documentRef);

                if (docSnap.exists()) {
                    setDocumentData(docSnap.data());

                    if (docSnap.data().account_details) {
                        setAccountDetails(docSnap.data().account_details);
                    }
                }
            }

            setLoading(false);
        };
        loadData();
    }, []);

    // -------------------------------------------
    // Upload file
    // -------------------------------------------
    const uploadFile = async (file, key, folder) => {
        return new Promise((resolve, reject) => {
            const storageRef = ref(storage, `${folder}/${userId}_${propertyId}_${file.name}`);

            setIsUploading(true);
            setUploadProgress(0);

            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(progress.toFixed(0));
                },
                (error) => {
                    setIsUploading(false);
                    reject(error);
                },
                async () => {
                    const url = await getDownloadURL(uploadTask.snapshot.ref);
                    setIsUploading(false);
                    resolve(url);
                }
            );
        });
    };

    // -------------------------------------------
    // Update Firestore
    // -------------------------------------------
    const updateDocumentStatus = async (key, url) => {
        const documentRef = doc(db, "documents", `${userId}_${propertyId}`);

        await setDoc(documentRef, {
            ...documentData,
            [key]: {
                url: url,
                status: "pending"    // Reset status after re-upload
            },
            account_details: accountDetails,
            userId,
            propertyId,
            status: "pending"
        }, { merge: true });

        setMessage("Document updated successfully!");

        setDocumentData(prev => ({
            ...prev,
            [key]: { url, status: "pending" }
        }));
    };

    // -------------------------------------------
    // Table Fields
    // -------------------------------------------
    const fields = [
        { key: "gst", label: "GST", folder: "business_doc" },
        { key: "msme", label: "MSME", folder: "business_doc" },
        { key: "shopact", label: "Shop Act", folder: "business_doc" },
        { key: "any_other", label: "Any Other", folder: "business_doc" },
        { key: "pan_tan", label: "PAN / TAN", folder: "address_proof" },
        { key: "bank_statement", label: "Bank Statement", folder: "bank_details" },
        { key: "cancelled_cheque", label: "Cancelled Cheque", folder: "bank_details" },
        { key: "passbook", label: "Passbook", folder: "bank_details" }
    ];

    // -------------------------------------------
    // Upload Handler
    // -------------------------------------------
    const handleUpload = async (e, field) => {
        const file = e.target.files[0];
        if (!file) return;

        const url = await uploadFile(file, field.key, field.folder);

        await updateDocumentStatus(field.key, url);
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: 4, alignItems: "center", justifyContent: "center" }}>
                <Typography sx={{ color: colors.vanilla }}>Loading...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }}>
            <Box component="main" sx={{ flexGrow: 1, maxWidth: "1600px", margin: "0 auto", width: "100%" }}>
                <Typography variant="h5" fontWeight={800} mb={3} sx={{ color: colors.vanilla }}>
                    Upload Documents
                </Typography>
                
                {message && <Alert severity="success" sx={{ mb: 3, bgcolor: colors.calPolyGreen, color: colors.vanilla, border: `1px solid ${colors.goldenrod}` }}>{message}</Alert>}
                
                {isUploading && (
                    <Paper sx={{ p: 2, mb: 3, background: colors.calPolyGreen, border: `1px solid ${colors.goldenrod}`, color: colors.vanilla }} elevation={1}>
                        <Typography sx={{ mb: 1 }}><b>Uploading Document...</b></Typography>

                        <Box sx={{ width: "100%", background: colors.darkGreen, height: 10, borderRadius: 1, border: `1px solid ${colors.goldenrod}` }}>
                            <Box
                                sx={{
                                    width: `${uploadProgress}%`,
                                    height: "100%",
                                    background: colors.goldenrod,
                                    borderRadius: 1,
                                    transition: "0.3s"
                                }}
                            ></Box>
                        </Box>

                        <Typography sx={{ mt: 1 }}>{uploadProgress}%</Typography>
                    </Paper>
                )}

                <div className="container-fluid p-0">
                    <div className="row">
                        {/* -------------------- TABLE -------------------- */}
                        <div className="col-sm-6 mb-3">
                            <Paper sx={{ p: 3, borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }} elevation={0}>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: colors.darkGreen }}>
                                            <TableRow>
                                                <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Document Name</TableCell>
                                                <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Upload / Re-upload</TableCell>
                                                <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Status</TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {fields.map((f) => {
                                                const info = documentData[f.key];
                                                const status = info?.status || "pending";

                                                return (
                                                    <TableRow key={f.key} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                                                        {/* Name */}
                                                        <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{f.label}</TableCell>

                                                        {/* Upload Button */}
                                                        <TableCell sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                                                            {status === "approved" ? (
                                                                <Button
                                                                    variant="contained"
                                                                    size="small"
                                                                    sx={{ 
                                                                        bgcolor: colors.calPolyGreen, 
                                                                        color: colors.vanilla, 
                                                                        border: `1px solid ${colors.goldenrod}`,
                                                                        fontWeight: 600, 
                                                                        textTransform: "none", 
                                                                        borderRadius: "8px",
                                                                        '&:hover': { bgcolor: colors.darkGreen } 
                                                                    }}
                                                                    onClick={() => window.open(info?.url, "_blank")}
                                                                >
                                                                    View
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    component="label"
                                                                    startIcon={<UploadFileIcon />}
                                                                    sx={{ 
                                                                        borderColor: colors.goldenrod, 
                                                                        color: colors.vanilla, 
                                                                        fontWeight: 600, 
                                                                        textTransform: "none", 
                                                                        borderRadius: "8px",
                                                                        '&:hover': { borderColor: colors.gold, bgcolor: 'rgba(255,255,255,0.05)' } 
                                                                    }}
                                                                >
                                                                    {info?.url ? "Re-upload" : "Upload"}
                                                                    <input
                                                                        type="file"
                                                                        hidden
                                                                        accept=".pdf,.jpg,.png"
                                                                        onChange={(e) => handleUpload(e, f)}
                                                                    />
                                                                </Button>
                                                            )}
                                                        </TableCell>

                                                        {/* Status */}
                                                        <TableCell sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                                                            <Typography
                                                                sx={{
                                                                    color:
                                                                        status === "approved"
                                                                            ? "#4ade80"
                                                                            : status === "rejected"
                                                                            ? "#f87171"
                                                                            : colors.gold,
                                                                    fontWeight: 600,
                                                                    fontSize: "0.85rem"
                                                                }}
                                                            >
                                                                {status.charAt(0).toUpperCase() + status.slice(1)}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </div>

                        {/* -------------------- BANK ACCOUNT -------------------- */}
                        <div className="col-sm-6 mb-3">
                            <Paper sx={{ p: 3, borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }} elevation={0}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: colors.vanilla }}>Account Information</Typography>

                                <TextField 
                                    fullWidth 
                                    size="small" 
                                    label="Account Number"
                                    sx={{ mb: 2 }}
                                    value={accountDetails.acc_no}
                                    onChange={(e) => setAccountDetails({ ...accountDetails, acc_no: e.target.value })}
                                    InputLabelProps={{ style: { color: colors.vanilla } }}
                                    InputProps={{ style: { color: colors.vanilla } }}
                                    sx={{ 
                                        mb: 2,
                                        "& .MuiOutlinedInput-root": { 
                                            borderRadius: "8px",
                                            backgroundColor: colors.darkGreen,
                                            "& fieldset": { borderColor: colors.goldenrod },
                                            "&:hover fieldset": { borderColor: colors.gold },
                                            "&.Mui-focused fieldset": { borderColor: colors.gold }
                                        } 
                                    }}
                                />

                                <TextField 
                                    fullWidth 
                                    size="small" 
                                    label="Account Holder Name"
                                    sx={{ mb: 2 }}
                                    value={accountDetails.acc_name}
                                    onChange={(e) => setAccountDetails({ ...accountDetails, acc_name: e.target.value })}
                                    InputLabelProps={{ style: { color: colors.vanilla } }}
                                    InputProps={{ style: { color: colors.vanilla } }}
                                    sx={{ 
                                        mb: 2,
                                        "& .MuiOutlinedInput-root": { 
                                            borderRadius: "8px",
                                            backgroundColor: colors.darkGreen,
                                            "& fieldset": { borderColor: colors.goldenrod },
                                            "&:hover fieldset": { borderColor: colors.gold },
                                            "&.Mui-focused fieldset": { borderColor: colors.gold }
                                        } 
                                    }}
                                />

                                <TextField 
                                    fullWidth 
                                    size="small" 
                                    label="IFSC Code"
                                    sx={{ mb: 2 }}
                                    value={accountDetails.ifsc_code}
                                    onChange={(e) => setAccountDetails({ ...accountDetails, ifsc_code: e.target.value })}
                                    InputLabelProps={{ style: { color: colors.vanilla } }}
                                    InputProps={{ style: { color: colors.vanilla } }}
                                    sx={{ 
                                        mb: 2,
                                        "& .MuiOutlinedInput-root": { 
                                            borderRadius: "8px",
                                            backgroundColor: colors.darkGreen,
                                            "& fieldset": { borderColor: colors.goldenrod },
                                            "&:hover fieldset": { borderColor: colors.gold },
                                            "&.Mui-focused fieldset": { borderColor: colors.gold }
                                        } 
                                    }}
                                />

                                <TextField 
                                    fullWidth 
                                    size="small" 
                                    label="Branch"
                                    sx={{ mb: 2 }}
                                    value={accountDetails.branch}
                                    onChange={(e) => setAccountDetails({ ...accountDetails, branch: e.target.value })}
                                    InputLabelProps={{ style: { color: colors.vanilla } }}
                                    InputProps={{ style: { color: colors.vanilla } }}
                                    sx={{ 
                                        mb: 2,
                                        "& .MuiOutlinedInput-root": { 
                                            borderRadius: "8px",
                                            backgroundColor: colors.darkGreen,
                                            "& fieldset": { borderColor: colors.goldenrod },
                                            "&:hover fieldset": { borderColor: colors.gold },
                                            "&.Mui-focused fieldset": { borderColor: colors.gold }
                                        } 
                                    }}
                                />

                                <Button
                                    variant="contained"
                                    onClick={async () => {
                                        await updateDocumentStatus("account_details", accountDetails);
                                        setMessage("Account details updated!");
                                    }}
                                    sx={{ 
                                        bgcolor: colors.goldenrod, 
                                        color: colors.darkGreen, 
                                        fontWeight: 700, 
                                        textTransform: "none", 
                                        borderRadius: "8px",
                                        '&:hover': { bgcolor: colors.gold } 
                                    }}
                                >
                                    Save Account Details
                                </Button>
                            </Paper>
                        </div>
                    </div>
                </div>
            </Box>
        </Box>
    );
}

export default Uploaddocuments;